import { z } from 'zod';
import { archiveProviderSchema, cameraTransportSchema, } from './cameras.js';
import { messageKey } from './messages.js';
/**
 * Видеорегистратор: узел типа `nvr` с многими каналами (ADR 037).
 *
 * Канал регистратора — это строка `cameras` на том же узле с номером
 * канала. Поток, тикет, аренда зрителя, раскладки видеостены и архив
 * работают по идентификатору камеры и про регистраторы не знают ничего.
 * Здесь описано только то, что у регистратора своё: как с ним говорить
 * и как определять его каналы.
 */
/**
 * Как разговаривать с регистратором.
 *
 * `dahua` и `hikvision` — HTTP API производителя: из него берутся список
 * каналов и их имена, а пути потоков известны заранее. `rtsp` — всё,
 * у чего API нет или оно закрыто: пути задаются шаблоном, а каналы
 * определяются перебором через медиасервер.
 */
export const RECORDER_PROTOCOLS = ['dahua', 'hikvision', 'rtsp'];
export const recorderProtocolSchema = z.enum(RECORDER_PROTOCOLS);
/**
 * Сколько каналов перебирать по умолчанию.
 *
 * Тридцать два — самый частый потолок регистраторов в стойке: больше
 * бывает, но редко, и перебор сверх нужного стоит времени — каждый
 * молчащий канал это ожидание ответа медиасервера.
 */
export const RECORDER_DEFAULT_MAX_CHANNELS = 32;
/** Предел, общий с настройкой архива `channel`: больше каналов не бывает. */
export const RECORDER_MAX_CHANNELS = 256;
/** Подстановка номера канала в шаблоне пути RTSP-регистратора. */
export const CHANNEL_PLACEHOLDER = '{channel}';
/**
 * Шаблон пути RTSP: `/cam/realmonitor?channel={channel}&subtype=0`.
 *
 * Те же ограничения, что у пути камеры, и по той же причине: путь
 * подставляется в URL источника для go2rtc, а решётка там отделяет
 * параметры источника от адреса.
 */
const channelTemplateSchema = z
    .string()
    .trim()
    .min(1, messageKey('validation.recorder.templateRequired'))
    .max(512)
    .refine((value) => value.startsWith('/'), messageKey('validation.recorder.templateSlash'))
    .refine((value) => !/[\s\\#]/.test(value), messageKey('validation.recorder.templateChars'))
    .refine((value) => value.includes(CHANNEL_PLACEHOLDER), messageKey('validation.recorder.templateChannel'));
const portSchema = z.coerce.number().int().min(1).max(65535);
const maxChannelsSchema = z.coerce.number().int().min(1).max(RECORDER_MAX_CHANNELS);
/**
 * Канал в составе регистратора.
 *
 * `available` — ответил ли канал при последнем определении: `null` — не
 * проверяли. Канал без камеры в списке остаётся, а не прячется: пустой
 * вход регистратора — законное состояние, и администратор должен видеть
 * его на месте, а не гадать, куда делся восьмой канал.
 */
export const recorderChannelSchema = z.object({
    cameraId: z.uuid(),
    channel: z.number().int(),
    name: z.string().nullable(),
    available: z.boolean().nullable(),
});
export const recorderSchema = z.object({
    id: z.uuid(),
    nodeId: z.uuid(),
    nodeName: z.string(),
    siteId: z.uuid(),
    protocol: recorderProtocolSchema,
    /** Порт веб-интерфейса: API Dahua и ISAPI живут на нём, а не на RTSP. */
    httpPort: z.number().int(),
    scheme: z.enum(['http', 'https']),
    rtspPort: z.number().int(),
    transport: cameraTransportSchema,
    mainTemplate: z.string().nullable(),
    subTemplate: z.string().nullable(),
    maxChannels: z.number().int(),
    archiveProvider: archiveProviderSchema.nullable(),
    archiveConfig: z.record(z.string(), z.unknown()),
    isEnabled: z.boolean(),
    lastDetectAt: z.iso.datetime().nullable(),
    lastDetectError: z.string().nullable(),
    /** Привязаны ли к узлу учётные данные RTSP — ими же ходят в API регистратора. */
    hasCredential: z.boolean(),
    channels: z.array(recorderChannelSchema),
});
export const createRecorderSchema = z
    .object({
    nodeId: z.uuid(),
    protocol: recorderProtocolSchema,
    httpPort: portSchema.default(80),
    scheme: z.enum(['http', 'https']).default('http'),
    rtspPort: portSchema.default(554),
    transport: cameraTransportSchema.default('tcp'),
    mainTemplate: channelTemplateSchema.nullish(),
    subTemplate: channelTemplateSchema.nullish(),
    maxChannels: maxChannelsSchema.default(RECORDER_DEFAULT_MAX_CHANNELS),
    archiveProvider: archiveProviderSchema.nullish(),
    archiveConfig: z.record(z.string(), z.unknown()).default({}),
    isEnabled: z.boolean().default(true),
})
    .superRefine((value, context) => {
    if (value.protocol === 'rtsp' && (value.mainTemplate ?? null) === null) {
        context.addIssue({
            code: 'custom',
            path: ['mainTemplate'],
            message: messageKey('validation.recorder.templateRequired'),
        });
    }
});
/**
 * Изменение регистратора.
 *
 * Поля перечислены заново, без `.partial()` от создания, по той же причине,
 * что и у камеры: иначе пустой `PATCH` сбросил бы порты к умолчаниям.
 * Сочетание «RTSP без шаблона» проверяет сервис — здесь видна только
 * половина регистратора.
 */
export const updateRecorderSchema = z.object({
    protocol: recorderProtocolSchema.optional(),
    httpPort: portSchema.optional(),
    scheme: z.enum(['http', 'https']).optional(),
    rtspPort: portSchema.optional(),
    transport: cameraTransportSchema.optional(),
    mainTemplate: channelTemplateSchema.nullish(),
    subTemplate: channelTemplateSchema.nullish(),
    maxChannels: maxChannelsSchema.optional(),
    archiveProvider: archiveProviderSchema.nullish(),
    archiveConfig: z.record(z.string(), z.unknown()).optional(),
    isEnabled: z.boolean().optional(),
});
/**
 * Итог определения каналов.
 *
 * Неудача — это заполненный ответ, а не отказ запроса, как у зондирования
 * камеры: регистратор не ответил, и администратору нужна причина рядом
 * с кнопкой, а каналы, найденные прошлый раз, остаются на месте.
 */
export const recorderDetectResultSchema = z.object({
    recorder: recorderSchema,
    found: z.number().int(),
    added: z.number().int(),
    removed: z.number().int(),
    viaAgent: z.string().nullable(),
    error: z.string().nullable(),
    at: z.iso.datetime(),
});
/**
 * Пути основного потока и substream канала.
 *
 * Для Dahua и Hikvision они известны заранее и одинаковы у всех прошивок,
 * которые встречались: `/cam/realmonitor?channel=N&subtype=0|1` и
 * `/Streaming/Channels/N01|N02`. Для RTSP — подстановка в шаблон.
 */
export function recorderStreamPaths(recorder, channel) {
    switch (recorder.protocol) {
        case 'dahua':
            return {
                main: `/cam/realmonitor?channel=${channel}&subtype=0`,
                sub: `/cam/realmonitor?channel=${channel}&subtype=1`,
            };
        case 'hikvision':
            return {
                main: `/Streaming/Channels/${channel}01`,
                sub: `/Streaming/Channels/${channel}02`,
            };
        case 'rtsp':
            return {
                main: fillChannel(recorder.mainTemplate ?? '', channel),
                sub: recorder.subTemplate === null ? null : fillChannel(recorder.subTemplate, channel),
            };
    }
}
/** Производитель строки камеры — от него зависят подписи и подсказки путей. */
export function recorderCameraVendor(protocol) {
    return protocol === 'rtsp' ? 'generic' : protocol;
}
/**
 * Настройки архива канала: общие настройки регистратора плюс номер канала.
 *
 * Номер ставится сразу в оба поля, которые читают провайдеры: `channel` —
 * Dahua и шаблон RTSP-повтора, `track` — Hikvision (`N01`, основной поток
 * канала). Дорожка ставится, только пока укладывается в свою схему: у
 * записи с неверным полем провайдер откатил бы к умолчаниям **все**
 * настройки, и сотый канал смотрел бы архив первого.
 */
export function channelArchiveConfig(base, channel) {
    const track = channel * 100 + 1;
    return {
        ...base,
        channel,
        ...(track <= 9999 ? { track } : {}),
    };
}
/** Провайдер архива каналов: у регистратора он один на все каналы. */
export function channelArchiveProvider(provider) {
    return provider;
}
function fillChannel(template, channel) {
    return template.split(CHANNEL_PLACEHOLDER).join(String(channel));
}
//# sourceMappingURL=recorders.js.map